import copy from '../../copy/en/shell.json' with { type: 'json' };
/**
 * Stable route destinations. Usable work areas precede the collapsed Planned
 * group (ADR 0007); roadmap order no longer controls everyday navigation.
 *
 * Items whose screens do not exist yet carry the phase that delivers them and
 * that phase's one-line goal (copied from the overview) so the placeholder page
 * can say exactly what arrives and when.
 */
import {
  BadgeDollarSign,
  BarChart3,
  Clapperboard,
  ClipboardCheck,
  FileSignature,
  CircleHelp,
  BookOpen,
  Gauge,
  Hospital,
  Inbox,
  LayoutDashboard,
  Mail,
  Network,
  Package,
  Plug,
  Receipt,
  Settings,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import type { PortalCapability } from "../../shared/roles.js";

export interface PhaseInfo {
  number: number;
  name: string;
  /** One-line goal from the overview's phase list. */
  goal: string;
  dependsOn: string;
}

export const PHASES: Readonly<Record<number, PhaseInfo>> = {
  0: {
    number: 0,
    name: copy.navManifest.foundationConventions,
    goal: copy.navManifest.mergeTheInFlightRbacAsThe,
    dependsOn: copy.navManifest.nothingGatesEverything,
  },
  1: {
    number: 1,
    name: copy.navManifest.partiesOrganizationHierarchy,
    goal: copy.navManifest.thePartyModelEffectiveDatedOrganizationRelationship,
    dependsOn: copy.navManifest.phase0,
  },
  2: {
    number: 2,
    name: copy.navManifest.practiceMaster,
    goal: copy.navManifest.oneCustomerRecordWithLocationsDoctorsContacts,
    dependsOn: copy.navManifest.phase0Phase1ForScopedVisibility,
  },
  3: {
    number: 3,
    name: copy.navManifest.productCatalogEnrollments,
    goal: copy.navManifest.theDynamicProductCatalogProductEnrollmentWith,
    dependsOn: copy.navManifest.phase2,
  },
  4: {
    number: 4,
    name: copy.navManifest.attributionAccountOwnership,
    goal: copy.navManifest.immutableAttributionWithHierarchySnapshotsEffectiveDated,
    dependsOn: copy.navManifest.phases123,
  },
  5: {
    number: 5,
    name: copy.navManifest.jotformImportIntegrationFramework,
    goal: copy.navManifest.integrationSourceIntegrationEventImportRunsWith,
    dependsOn: copy.navManifest.phases234AndTheJotform,
  },
  6: {
    number: 6,
    name: copy.navManifest.commissionPlansRulesEntitlements,
    goal: copy.navManifest.plansVersionsRulesEntitlementsTheDeterministicPrecedence,
    dependsOn: copy.navManifest.phases134,
  },
  7: {
    number: 7,
    name: copy.navManifest.revenueTransactionsTheImmutableLedger,
    goal: copy.navManifest.revenueTransactionEnteredInAppOrUploaded,
    dependsOn: copy.navManifest.phases56,
  },
  8: {
    number: 8,
    name: copy.navManifest.payments,
    goal: copy.navManifest.paymentRunsPayablePaidAccountingExportPer,
    dependsOn: copy.navManifest.phase7Phase10ForStatementDocuments,
  },
  9: {
    number: 9,
    name: copy.navManifest.crmSurfaces,
    goal: copy.navManifest.adminEditablePipelinesPerProductOpportunitiesTasks,
    dependsOn: copy.navManifest.phases234RicherWith7,
  },
  10: {
    number: 10,
    name: copy.navManifest.documentsTheESignModule,
    goal: copy.navManifest.aGeneralDocumentAcrossSigningArtifactsAnd,
    dependsOn: copy.navManifest.phases23IndependentOfTheCommission,
  },
  11: {
    number: 11,
    name: copy.navManifest.communications,
    goal: copy.navManifest.emailTemplatesSegmentsSchedulingAndDeliveryTracking,
    dependsOn: copy.navManifest.phases129,
  },
  12: {
    number: 12,
    name: copy.navManifest.exceptionsAutomation,
    goal: copy.navManifest.theExceptionEngineFirstSavedThresholdQueries,
    dependsOn: copy.navManifest.phase0JobsUsefulFromPhase7,
  },
  13: {
    number: 13,
    name: copy.navManifest.dashboardsReportingForecasting,
    goal: copy.navManifest.managerAggregatorDashboardExecutiveCommandCenterDrill,
    dependsOn: copy.navManifest.phases789,
  },
  16: {
    number: 16,
    name: copy.navManifest.externalSystemIntegrations,
    goal: copy.navManifest.theFewExternalSystemsThatExistZoom,
    dependsOn: copy.navManifest.phases57AnySingleAdapterMay,
  },
};

export interface NavItem {
  id: string;
  label: string;
  to: string;
  icon: LucideIcon;
  /** Present when the section's real screens arrive in a later phase. */
  upcomingPhase?: number;
  /** Capability required to see the item. The server remains authoritative. */
  capability?: PortalCapability;
  /** Interim entry hosting legacy features until its owning phase lands. */
  interim?: boolean;
  description: string;
}

/** Staff task groups; routes and capability gates remain the same. */
export const NAV_GROUPS = [
  { label: copy.navManifest.workspace, collapsible: false, ids: ["dashboard", "rep-workspace", "exceptions", "practices", "documents"] },
  { label: copy.navManifest.organization, collapsible: false, ids: ["sales-organization", "products"] },
  { label: copy.navManifest.resources, collapsible: false, ids: ["getting-started", "videos", "faqs"] },
  { label: copy.navManifest.manage, collapsible: true, ids: ["applications", "broadcasts", "integrations", "administration"] },
  { label: copy.navManifest.simulations, collapsible: true, ids: ["commissions", "revenue", "payments"] },
] as const;

/** Route inventory; each destination has a distinct glyph. NAV_ITEMS defines the workspace order. */
export const TOP_LEVEL_NAV: readonly NavItem[] = [
  { id: "dashboard", label: copy.navManifest.overview, to: "/", icon: LayoutDashboard, capability: "view:all", description: copy.navManifest.registeredPracticesUnresolvedWorkAndNetworkInsights },
  { id: "exceptions", label: copy.navManifest.inbox, to: "/exceptions", icon: Inbox, capability: "exceptions:read", description: copy.navManifest.reviewScopedFindingsTakeResponsibilityAndFollow },
  { id: "rep-workspace", label: copy.navManifest.dashboard, to: "/rep-workspace", icon: Gauge, capability: "reps:read", description: copy.navManifest.copyYourRegistrationLinkFollowUpWith },
  { id: "getting-started", label: copy.navManifest.gettingStarted, to: "/getting-started", icon: BookOpen, capability: "reps:read", description: copy.navManifest.sharePermanentPracticeAndRepresentativeInvitationsWith },
  { id: "practices", label: copy.navManifest.practices, to: "/practices", icon: Hospital, capability: "practices:view", description: copy.navManifest.findAPracticeReviewItsDetailsAnd },
  { id: "applications", label: copy.navManifest.applications, to: "/applications", icon: ClipboardCheck, capability: "integrations:manage", description: copy.navManifest.reviewActionableApplicationsAndOpenTheComplete },
  { id: "videos", label: copy.navManifest.videos, to: "/videos", icon: Clapperboard, description: copy.navManifest.watchAndShareChairshareVideos },
  { id: "faqs", label: copy.navManifest.faqs, to: "/faqs", icon: CircleHelp, description: copy.navManifest.readAndDownloadFaqs },
  { id: "sales-organization", label: copy.navManifest.organization, to: "/sales-organization", icon: Network, capability: "org:view", description: copy.navManifest.yourPeoplePartnersAndReportingRelationships },
  { id: "products", label: copy.navManifest.products, to: "/products", icon: Package, capability: "products:view", description: copy.navManifest.exploreProgramsConfigureProductsAndManageEnrollments },
  { id: "commissions", label: copy.navManifest.commissions, to: "/commissions", icon: BadgeDollarSign, capability: "commissions:manage", description: copy.navManifest.tryAGuidedMockExampleReviewPlan },
  { id: "revenue", label: copy.navManifest.revenue, to: "/revenue", icon: Receipt, capability: "commissions:manage", description: copy.navManifest.importMockActivityIndependentlyReviewCalculationsReconcile },
  { id: "payments", label: copy.navManifest.payments, to: "/payments", icon: Wallet, capability: "payments:manage", description: copy.navManifest.previewClosedMockEarningsIndependentlyApproveRuns },
  { id: "communications", label: copy.navManifest.communications, to: "/communications", icon: Mail, capability: "communications:read", description: copy.navManifest.prepareScopedMessagesReviewAudiencesSimulateDelivery },
  { id: "broadcasts", label: copy.navManifest.broadcasts, to: "/communications/broadcasts", icon: Mail, capability: "users:manage", description: copy.navManifest.preparePersonalizedOrganizationEmailsChooseRecipientsAnd },
  { id: "integrations", label: copy.navManifest.integrations, to: "/integrations", icon: Plug, capability: "integrations:manage", description: copy.navManifest.preserveJotformExportSnapshotsAndInspectSource },
  { id: "reports", label: copy.navManifest.reports, to: "/reports", icon: BarChart3, upcomingPhase: 13, description: copy.navManifest.dashboardsReportingAndForecasting },
  { id: "administration", label: copy.navManifest.administration, to: "/admin", icon: Settings, capability: "users:manage", description: copy.navManifest.usersSalesRolesCustomFieldsLegacyLinkage },
];

/** Scoped document evidence alongside the retained signing views. */
export const DOCUMENTS_NAV: NavItem = {
  id: "documents",
  label: copy.navManifest.documents,
  to: "/documents",
  icon: FileSignature,
  capability: "documents:read",
  description: copy.navManifest.practiceAndEnrollmentChecklistsPdfEvidenceReview,
};

const workingOrder: readonly string[] = NAV_GROUPS.flatMap(group => [...group.ids]);
export const NAV_ITEMS: readonly NavItem[] = [...TOP_LEVEL_NAV.filter(item => item.id !== "communications"), DOCUMENTS_NAV].sort((a, b) => {
  const order = (item: NavItem) => {
    const index = workingOrder.indexOf(item.id);
    return item.upcomingPhase !== undefined || index < 0 ? workingOrder.length : index;
  };
  return order(a) - order(b);
});

export function navItemById(id: string): NavItem | undefined {
  return NAV_ITEMS.find((i) => i.id === id);
}

export function phaseFor(item: NavItem): PhaseInfo | undefined {
  return item.upcomingPhase !== undefined ? PHASES[item.upcomingPhase] : undefined;
}
